import { useContextHotkeys } from "@/hooks/useContextTree";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Types
export interface ListItemRef<T> {
  id: string;
  node: HTMLElement;
  data: T;
}

export interface ListContextType<T> {
  // Data
  data: T[];
  filteredData: T[];

  // State
  filter: string;
  setFilter: (filter: string) => void;
  checkedRows: Record<string | number, boolean>;
  setCheckedRows: React.Dispatch<
    React.SetStateAction<Record<string | number, boolean>>
  >;
  activeIndex: number | null;
  setActiveIndex: (index: number) => void;
  setActiveIndexNoScroll: (index: number) => void;
  isKeyboardNavigation: boolean;
  setIsKeyboardNavigation: (value: boolean) => void;
  isMouseEnabled: boolean;
  enableMouse: () => void;
  isLoading: boolean;

  // Configuration
  getRowKey: (item: T) => string | number;
  getIsChecked?: (item: T, index?: number) => boolean;
  getShouldSkipRow?: (item: T) => boolean;
  filterOptions?: (options: T[], filter: string) => T[];
  isMultiselect: boolean;
  isCheckboxesEnabled: boolean;
  isNullable: boolean;
  maxCheckedRows?: number;
  size: "sm" | "md" | "lg";
  autofocus?: boolean;

  // Callbacks
  onItemClick?: (item: T) => void;
  onSelectMultiSelectOption?: (item: T) => void;
  onSubmitCheckedRows?: (
    checkedRows: T[],
    uncheckedRows: T[],
    selectedRow: T,
  ) => void;
  onNewRow?: (filter: string) => void | Promise<unknown>;
  /**
   * Submits the inline "Create new …" handler with the current filter.
   * Manages the ``isCreatingNewRow`` flag and clears the filter on
   * success so the keyboard Enter path and the ``ListCreateNewFooter``
   * click stay in lockstep.
   */
  submitNewRow: () => Promise<void>;
  isCreatingNewRow: boolean;
  onActiveIndexChange?: (data: T, onClick: () => void) => void;
  handleItemClick: (item: T, index: number) => void;

  // Internal refs and methods
  items: React.MutableRefObject<ListItemRef<T>[]>;
  containerRef: React.RefObject<HTMLElement | null>;
  registerItem: (id: string, node: HTMLElement, data: T) => () => void;
  moveHighlight: (dir: 1 | -1) => void;
  getItemId: (index: number) => string | undefined;
  scrollIntoView: (index: number) => void;
  scrollToOffset: ((index: number) => void) | null;
  setScrollToOffset: (fn: ((index: number) => void) | null) => void;
  itemHeight: number;
  setItemHeight: (height: number) => void;
  searchBarHeight: number;
  setSearchBarHeight: (height: number) => void;
  overscanCount: number;

  // Computed values
  uncheckedData: T[];
  checkedData: T[];
  navigationData: T[]; // For consistent keyboard navigation when checkboxes are enabled
  hasNewButton: boolean; // Whether there is a way to make one: the row at its foot, and its place after the last option

  // Styles
  /**
   * Each part's room, by the list's size: a row's, the rows' own, and a
   * group's sticky heading's; and ``text``, what a list writes in words of
   * its own - what is typed in its search, and the row at its foot that
   * makes one, which names it: one size, given here once, a list's text.
   */
  classes: {
    listItem: string;
    listItems: string;
    listGroup: string;
    text: string;
  };
}

/**
 * Each size's room round a row, in the theme's steps (``sizeSpacing``): a
 * row's padding, the rows', and a group's sticky heading running its line
 * out through the rows' padding to the list's edges - its text still over
 * theirs - and leaving that padding again under the line before the group's
 * first row. A list's text is body-md's.
 */
const LIST_CLASSES = {
  sm: {
    listItem: "pl-1 pr-1 pt-1 pb-1",
    listItems: "p-1",
    listGroup: "-ml-1 -mr-1 pl-2 pr-2 pt-1 pb-1 mb-1",
  },
  md: {
    listItem: "pl-1.5 pr-1.5 pt-1.5 pb-1.5",
    listItems: "p-1.5",
    listGroup: "-ml-1.5 -mr-1.5 pl-3 pr-3 pt-1.5 pb-1.5 mb-1.5",
  },
  lg: {
    listItem: "pl-2 pr-2 pt-2 pb-2",
    listItems: "p-2",
    listGroup: "-ml-2 -mr-2 pl-4 pr-4 pt-2 pb-2 mb-2",
  },
} as const;
const LIST_TEXT_CLASS = "font-medium text-sm leading-snug text-foreground";

// Preserve the context object across Vite HMR of this module. Otherwise the
// Provider remounts with a new context identity while already-mounted
// ListSearchInput / List.Items fibers still read the previous one →
// ``useListContext must be used within a List component``.
const ListContext: React.Context<ListContextType<any> | null> =
  (
    import.meta.hot?.data as {
      ListContext?: React.Context<ListContextType<any> | null>;
    }
  )?.ListContext ?? createContext<ListContextType<any> | null>(null);

if (import.meta.hot) {
  import.meta.hot.dispose((data: { ListContext?: typeof ListContext }) => {
    data.ListContext = ListContext;
  });
}

export function useListContext<T>(): ListContextType<T> {
  const context = React.useContext(ListContext);
  if (!context) {
    throw new Error("useListContext must be used within a List component");
  }
  return context;
}

// Internal hook for managing list state
export function useList<T>({
  data,
  getRowKey,
  getIsChecked = () => false,
  getShouldSkipRow = () => false,
  filterOptions,
  isMultiselect = false,
  isCheckboxesEnabled = false,
  isNullable = true,
  maxCheckedRows,
  isLoading = false,
  onItemClick,
  onSelectMultiSelectOption,
  onSubmitCheckedRows,
  onNewRow,
  newRowNeedsName = false,
  onActiveIndexChange,
  size = "sm",
  autofocus = false,
  containerRef,
  overscanCount = 1,
}: {
  data: T[];
  getRowKey: (item: T) => string | number;
  getIsChecked?: (item: T, index?: number) => boolean;
  getShouldSkipRow?: (item: T) => boolean;
  filterOptions?: (options: T[], filter: string) => T[];
  isMultiselect?: boolean;
  isCheckboxesEnabled?: boolean;
  isNullable?: boolean;
  maxCheckedRows?: number;
  isLoading?: boolean;
  onItemClick?: (item: T) => void;
  onSelectMultiSelectOption?: (item: T) => void;
  onSubmitCheckedRows?: (
    checkedRows: T[],
    uncheckedRows: T[],
    selectedRow: T,
  ) => void;
  onNewRow?: (filter: string) => void | Promise<unknown>;
  newRowNeedsName?: boolean;
  onActiveIndexChange?: (data: T, onClick: () => void) => void;
  size?: "sm" | "md" | "lg";
  autofocus?: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  overscanCount?: number;
}) {
  // Size-based spacing values (in theme spacing units)
  const sizeSpacing = { sm: 0.5, md: 0.75, lg: 1 }[size];
  // Convert to pixels for scroll calculations
  const sizeSpacingPx = sizeSpacing * 8;
  const classes = useMemo(
    () => ({ ...LIST_CLASSES[size], text: LIST_TEXT_CLASS }),
    [size],
  );

  // State
  const [filter, setFilter] = useState("");
  const [checkedRows, setCheckedRows] = useState<
    Record<string | number, boolean>
  >(
    isMultiselect
      ? data.reduce((acc, item) => {
          acc[getRowKey(item)] = getIsChecked(item);
          return acc;
        }, {})
      : {},
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [isMouseEnabled, setIsMouseEnabled] = useState(true);
  // Tracks an in-flight inline "Create new …" submission so both the
  // button click and the Enter-key handler can show a spinner and
  // disable themselves; lifted into the list state (rather than kept
  // local in ``ListNewItemButton``) so the keyboard path stays in sync.
  const [isCreatingNewRow, setIsCreatingNewRow] = useState(false);

  // Minimum loading duration to prevent flickering (500ms)
  const [isMinLoadingActive, setIsMinLoadingActive] = useState(false);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Start minimum loading period
      setIsMinLoadingActive(true);
      // Clear any existing timeout and start a new one
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      loadingTimeoutRef.current = setTimeout(() => {
        setIsMinLoadingActive(false);
      }, 200);
    }
    // Only cleanup on unmount, not when isLoading changes
  }, [isLoading]);

  // Cleanup timeout on unmount only
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Show loading if either actually loading OR still in minimum display period
  const displayLoading = isLoading || isMinLoadingActive;

  // Enable mouse events (called when mouse actually moves)
  const enableMouse = useCallback(() => {
    if (!isMouseEnabled) {
      setIsMouseEnabled(true);
    }
  }, [isMouseEnabled]);

  // Refs
  const items = useRef<ListItemRef<T>[]>([]);

  // Ref for scrollToOffset function from virtualizer - use ref to avoid stale closures
  const scrollToOffsetRef = useRef<((index: number) => void) | null>(null);

  // State for scrollToOffset (exposed in context for setting)
  const [scrollToOffset, setScrollToOffsetState] = useState<
    ((index: number) => void) | null
  >(null);

  // Custom setter that updates both ref and state
  const setScrollToOffset = useCallback(
    (fn: ((index: number) => void) | null) => {
      scrollToOffsetRef.current = fn;
      setScrollToOffsetState(fn);
    },
    [],
  );

  // State for search bar height
  const [searchBarHeight, setSearchBarHeight] = useState<number>(0);

  // State for item height (set by ListItems after measuring)
  const [itemHeight, setItemHeight] = useState<number>(32);

  // Computed values
  const filteredData = filterOptions ? filterOptions(data, filter) : data;
  const uncheckedData =
    isMultiselect && isCheckboxesEnabled
      ? [...filteredData].sort((a, b) => {
          const aChecked = checkedRows[getRowKey(a)] ? 1 : 0;
          const bChecked = checkedRows[getRowKey(b)] ? 1 : 0;
          return bChecked - aChecked;
        })
      : filteredData?.filter(
          (option) => !isMultiselect || !checkedRows[getRowKey(option)],
        );
  const checkedData = filteredData
    ?.filter((option) => isMultiselect && checkedRows[getRowKey(option)])
    ?.sort((a) => (checkedRows[getRowKey(a)] ? -1 : 1));

  const navigationData = uncheckedData;

  // Item registration
  const registerItem = useCallback((id: string, node: HTMLElement, data: T) => {
    const itemRef: ListItemRef<T> = { id, node, data };
    items.current.push(itemRef);

    // Cleanup on unmount
    return () => {
      items.current = items.current.filter((item) => item.id !== id);
    };
  }, []);

  // Navigation - a place after the last option for the way to make one, there
  // whenever that row is (``ListCreateNewFooter``): always, but for a list
  // that makes one only from what is typed.
  const hasNewButton =
    !!onNewRow && !displayLoading && (!!filter || !newRowNeedsName);
  const totalNavigableCount = navigationData.length + (hasNewButton ? 1 : 0);

  const moveHighlight = useCallback(
    (dir: 1 | -1) => {
      if (!totalNavigableCount) return;
      setIsKeyboardNavigation(true);
      setIsMouseEnabled(false); // Disable mouse until it actually moves
      setActiveIndex((prev) => {
        if (prev === null) return 0;
        let next = prev + dir;
        if (next < 0) next = totalNavigableCount - 1;
        if (next >= totalNavigableCount) next = 0;
        return next;
      });
    },
    [totalNavigableCount],
  );

  // Set active index without triggering scroll (for mouse events)
  const setActiveIndexNoScroll = useCallback((index: number) => {
    setActiveIndex(index);
    setIsKeyboardNavigation(false); // Reset keyboard navigation flag when mouse moves
  }, []);

  // Scroll active item into view
  const scrollIntoView = useCallback(
    (index: number) => {
      if (index < 0 || index >= navigationData.length) return;

      const activeItem = navigationData[index];
      const itemKey = getRowKey(activeItem);
      const itemRef = items.current.find(
        (item) => getRowKey(item.data) === itemKey,
      );

      if (itemRef?.node) {
        const container = containerRef.current;
        if (container) {
          const itemRect = itemRef.node.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (
            itemRect.top >= containerRect.top &&
            itemRect.bottom <= containerRect.bottom
          ) {
            return;
          }
        }
        itemRef.node.scrollIntoView({ block: "nearest", behavior: "instant" });
        return;
      }

      // Fallback: Calculate position for virtualized items not in DOM
      const container = containerRef.current;
      if (!container) return;
      const containerHeight = container.clientHeight;
      const estimatedItemTop = index * itemHeight;

      container.scrollTo({
        top: Math.max(
          0,
          estimatedItemTop - containerHeight / 2 + itemHeight / 2,
        ),
        behavior: "instant",
      });
    },
    [navigationData, containerRef, itemHeight, getRowKey],
  );

  // Scroll active item into view when activeIndex changes via keyboard
  useEffect(() => {
    if (activeIndex !== null && isKeyboardNavigation) {
      scrollIntoView(activeIndex);
      // Don't reset the flag here - let it be reset by mouse movement
    }
  }, [activeIndex, scrollIntoView, isKeyboardNavigation]);

  // Call onActiveIndexChange when activeIndex changes
  useEffect(() => {
    if (
      activeIndex !== null &&
      navigationData?.[activeIndex] &&
      onActiveIndexChange
    ) {
      const activeItem = navigationData[activeIndex];
      const shouldSkip = getShouldSkipRow(activeItem);

      if (!shouldSkip) {
        const onClick = () => {
          if (onItemClick) {
            onItemClick(activeItem);
          }
        };
        onActiveIndexChange(activeItem, onClick);
      }
    }
  }, [
    activeIndex,
    navigationData,
    onActiveIndexChange,
    getShouldSkipRow,
    onItemClick,
  ]);

  const getItemId = useCallback((index: number) => {
    return items.current[index]?.id;
  }, []);

  // Handle item click
  const handleItemClick = useCallback(
    (item: T, index: number) => {
      const shouldSkip = getShouldSkipRow(item);
      if (shouldSkip) return;

      if (isMultiselect) {
        const currentKey = getRowKey(item);
        const isCurrentlyChecked = checkedRows[currentKey];
        const checkedCount = Object.values(checkedRows).filter(Boolean).length;
        const isBlockingUncheck =
          isCheckboxesEnabled &&
          !isNullable &&
          isCurrentlyChecked &&
          checkedCount === 1;

        if (isBlockingUncheck) {
          return;
        }

        if (!isCurrentlyChecked && maxCheckedRows === 1) {
          setCheckedRows({ [currentKey]: true });
          onSelectMultiSelectOption?.(item);

          if (isCheckboxesEnabled && index === checkedCount) {
            const newIndex = Math.min(
              checkedCount + 1,
              filteredData.length - 1,
            );
            setActiveIndex(newIndex);
            setIsKeyboardNavigation(true);
          }
          return;
        }

        // Toggle checkbox state for multiselect
        setCheckedRows((prev) => ({
          ...prev,
          [currentKey]: !isCurrentlyChecked,
        }));
        onSelectMultiSelectOption?.(item);

        // When checking the first unchecked item, advance active index to the next unchecked row
        if (
          isCheckboxesEnabled &&
          !isCurrentlyChecked &&
          index === checkedCount
        ) {
          const newCheckedCount = checkedCount + 1;
          const newIndex = Math.min(newCheckedCount, filteredData.length - 1);
          setActiveIndex(newIndex);
          setIsKeyboardNavigation(true);
        }
      } else if (onItemClick) {
        onItemClick(item);
      }
    },
    [
      getShouldSkipRow,
      setActiveIndex,
      isMultiselect,
      setCheckedRows,
      getRowKey,
      checkedRows,
      isCheckboxesEnabled,
      isNullable,
      maxCheckedRows,
      onItemClick,
      onSelectMultiSelectOption,
      filteredData,
    ],
  );

  // Keyboard event handling
  const handleArrowDown = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      moveHighlight(1);
    },
    [moveHighlight],
  );

  const handleArrowUp = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      moveHighlight(-1);
    },
    [moveHighlight],
  );

  // Single submission point shared by the button click and the
  // keyboard ``Enter`` handler.
  //
  // We clear the filter *synchronously* — before awaiting the (possibly
  // long) network call — so optimistic-UI handlers (which call
  // ``events.onChangeField`` with a temp row in the same tick) see the
  // picker immediately reflect their write rather than holding the
  // search term up for the round-trip. The ``isCreatingNewRow`` flag
  // is still kept around as a guard against double-submits on very
  // fast hardware where the user could press Enter twice before the
  // first network resolves.
  //
  // Rejections are swallowed because the ``onNewRow`` implementation
  // owns its own error UX (e.g. snackbar + chip rollback via
  // ``onChangeField``) — re-throwing would surface as an unhandled
  // promise on the keyboard path.
  const submitNewRow = useCallback(async () => {
    if (!onNewRow || isCreatingNewRow) return;
    const currentFilter = filter || "";
    setIsCreatingNewRow(true);
    setFilter("");
    try {
      await onNewRow(currentFilter);
    } catch {
      // intentionally swallowed; handler owns its own error UX
    } finally {
      setIsCreatingNewRow(false);
    }
  }, [onNewRow, filter, isCreatingNewRow]);

  const handleEnter = useCallback(
    (event: KeyboardEvent) => {
      // Don't handle enter if meta/cmd key is pressed (let meta+enter handlers take over)
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      event.preventDefault();
      if (activeIndex === null) return;

      // Check if the "Add New" button is selected (last slot when onNewRow is set)
      if (hasNewButton && activeIndex === navigationData.length) {
        void submitNewRow();
        return;
      }

      if (navigationData && navigationData[activeIndex]) {
        const activeItem = navigationData[activeIndex];
        handleItemClick(activeItem, activeIndex);
      }
    },
    [activeIndex, navigationData, handleItemClick, hasNewButton, submitNewRow],
  );

  // Register hotkeys
  useContextHotkeys({
    id: "list-arrow-down",
    combos: "arrowdown",
    callback: handleArrowDown,
  });
  useContextHotkeys({
    id: "list-arrow-up",
    combos: "arrowup",
    callback: handleArrowUp,
  });
  useContextHotkeys({
    id: "list-enter",
    combos: "enter",
    callback: handleEnter,
  });

  // Reset checked rows when the option set changes. Read ``getIsChecked``
  // via a ref so a new callback identity from the parent (common when a
  // portaled picker causes the anchor row to re-render) does not wipe the
  // user's in-progress selection before Submit / ⌘↵.
  const getIsCheckedRef = useRef(getIsChecked);
  getIsCheckedRef.current = getIsChecked;
  const getRowKeyRef = useRef(getRowKey);
  getRowKeyRef.current = getRowKey;

  useEffect(() => {
    if (isMultiselect) {
      setCheckedRows(
        data.reduce(
          (acc, item) => {
            acc[getRowKeyRef.current(item)] = getIsCheckedRef.current(item);
            return acc;
          },
          {} as Record<string | number, boolean>,
        ),
      );
    }
  }, [isMultiselect, data?.length]);

  // Reset active index when data changes - default to first item for command palette UX.
  // Nothing matching what is typed, the row that creates it is the first - Enter makes it.
  const isOnlyCreating = hasNewButton && !!filter;
  useEffect(() => {
    if ((filteredData && filteredData.length > 0) || isOnlyCreating) {
      setActiveIndex(0);
    } else {
      setActiveIndex(null);
    }
  }, [filteredData?.length, isOnlyCreating]);

  return {
    // Data
    data,
    filteredData,

    // State
    filter,
    setFilter,
    checkedRows,
    setCheckedRows,
    activeIndex,
    setActiveIndex,
    setActiveIndexNoScroll,
    isKeyboardNavigation,
    setIsKeyboardNavigation,
    isMouseEnabled,
    enableMouse,
    isLoading: displayLoading,

    // Configuration
    getRowKey,
    getIsChecked,
    getShouldSkipRow,
    filterOptions,
    isMultiselect,
    isCheckboxesEnabled,
    isNullable,
    maxCheckedRows,
    size,
    autofocus,

    // Callbacks
    onItemClick,
    onSelectMultiSelectOption,
    onSubmitCheckedRows,
    onNewRow,
    submitNewRow,
    isCreatingNewRow,
    onActiveIndexChange,
    handleItemClick,

    // Internal
    items,
    containerRef,
    registerItem,
    moveHighlight,
    getItemId,
    scrollIntoView,
    scrollToOffset,
    setScrollToOffset,
    itemHeight,
    setItemHeight,
    searchBarHeight,
    setSearchBarHeight,

    // Computed
    uncheckedData,
    checkedData,
    navigationData,
    hasNewButton,

    // Virtualization
    overscanCount,

    // Styles
    classes,
  };
}

export { ListContext };
